/**
 * Shared countdown timer for homepage and giveaway banners.
 * Uses one global interval instead of one interval per element.
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    static instances = new Set();
    static tickerId = null;
    static visibilityBound = false;

    constructor() {
      super();
      this._completed = false;
      this._lastRenderedSecond = null;

      const timezone = this.dataset.timezone || '+0000';
      const date = (this.dataset.date || '').split('-');
      const day = parseInt(date[0], 10) || 1;
      const month = parseInt(date[1], 10) || 1;
      const year = parseInt(date[2], 10) || 1970;
      const time = this.dataset.time || '00:00';
      const [targetHour, targetMinute] = time.split(':').map((value) => parseInt(value, 10) || 0);

      this.countDownDate = new Date(`${month}/${day}/${year} ${targetHour}:${targetMinute} GMT${timezone}`).getTime();
    }

    connectedCallback() {
      this.displayMode = this.dataset.countdownDisplayMode || 'days_hours_minutes_seconds';
      this.daysEl = this.querySelector('.days .countdown-timer--column--number');
      this.hoursEl = this.querySelector('.hours .countdown-timer--column--number');
      this.minutesEl = this.querySelector('.minutes .countdown-timer--column--number');
      this.secondsEl = this.querySelector('.seconds .countdown-timer--column--number');

      this.render(Date.now());
      CountdownTimer.instances.add(this);
      CountdownTimer.ensureTicker();
    }

    disconnectedCallback() {
      CountdownTimer.instances.delete(this);
      CountdownTimer.stopTickerIfIdle();
    }

    render(now) {
      const distance = this.countDownDate - now;
      const currentSecond = Math.max(Math.floor(distance / 1000), 0);

      if (this._lastRenderedSecond === currentSecond) {
        return;
      }

      this._lastRenderedSecond = currentSecond;

      if (distance < 0) {
        this._completed = true;
        this.updateColumns(0, 0, 0, 0);
        CountdownTimer.stopTickerIfIdle();
        return;
      }

      this._completed = false;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = this.displayMode === 'hours_minutes_seconds'
        ? Math.floor(distance / (1000 * 60 * 60))
        : Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.updateColumns(days, hours, minutes, seconds);
    }

    updateColumns(days, hours, minutes, seconds) {
      if (this.daysEl) {
        this.daysEl.textContent = CountdownTimer.addZero(days);
      }
      if (this.hoursEl) {
        this.hoursEl.textContent = CountdownTimer.addZero(hours);
      }
      if (this.minutesEl) {
        this.minutesEl.textContent = CountdownTimer.addZero(minutes);
      }
      if (this.secondsEl) {
        this.secondsEl.textContent = CountdownTimer.addZero(seconds);
      }
    }

    static ensureTicker() {
      if (!this.visibilityBound) {
        this.visibilityBound = true;
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            this.stopTicker();
            return;
          }

          this.instances.forEach((instance) => instance.render(Date.now()));
          this.startTicker();
        });
      }

      this.startTicker();
    }

    static startTicker() {
      if (this.tickerId || document.visibilityState === 'hidden' || !this.instances.size) {
        return;
      }

      this.tickerId = window.setInterval(() => {
        const now = Date.now();
        let hasLiveInstance = false;

        this.instances.forEach((instance) => {
          if (instance._completed) {
            return;
          }

          hasLiveInstance = true;
          instance.render(now);
        });

        if (!hasLiveInstance) {
          this.stopTicker();
        }
      }, 1000);
    }

    static stopTicker() {
      if (!this.tickerId) {
        return;
      }

      clearInterval(this.tickerId);
      this.tickerId = null;
    }

    static stopTickerIfIdle() {
      const hasLiveInstance = Array.from(this.instances).some((instance) => !instance._completed);

      if (!hasLiveInstance) {
        this.stopTicker();
      }
    }

    static addZero(value) {
      return value < 10 && value >= 0 ? `0${value}` : String(value);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
